import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import { Input } from '../../design-system/Input';
import { Tabs } from '../../design-system/Tabs';
import { Modal } from '../../design-system/Modal';
import {
  Users,
  UserPlus,
  Mail,
  Trash2,
  Layers,
  Plus
} from 'lucide-react';

export const MembersPage = () => {
  const {
    activeWorkspace,
    members = [],
    pendingInvites = [],
    teams = [],
    updateMemberRole,
    removeMember,
    revokeInvite,
    createTeam,
    setIsInviteModalOpen
  } = useWorkspace();

  const [activeTab, setActiveTab] = useState('members');
  const [search, setSearch] = useState('');
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamIcon, setTeamIcon] = useState('group');

  const teamIcons = ['group', 'code', 'palette', 'memory', 'science', 'rocket_launch', 'terminal'];

  const tabs = [
    { id: 'members', label: `Active Members (${members.length})`, icon: <Users size={15} /> },
    { id: 'invites', label: `Pending Invites (${pendingInvites.length})`, icon: <Mail size={15} /> },
    { id: 'teams', label: `Teams & Groups (${teams.length})`, icon: <Layers size={15} /> }
  ];

  const filteredMembers = members.filter((m) =>
    (m.user?.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.user?.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.role || '').toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'owner':
        return 'teal';
      case 'admin':
        return 'purple';
      case 'member':
        return 'info';
      case 'guest':
      default:
        return 'neutral';
    }
  };

  const handleCreateTeamSubmit = (e) => {
    e?.preventDefault();
    if (!teamName.trim()) return;
    createTeam(teamName.trim(), teamIcon);
    setTeamName('');
    setIsTeamModalOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">Workspace Access</Badge>
            <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Members & Permissions</h1>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Manage collaborators, role-based access control, and team groups for <strong>{activeWorkspace?.name}</strong>
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
          <Button
            variant="primary"
            size="sm"
            icon={<UserPlus size={15} />}
            onClick={() => setIsInviteModalOpen(true)}
          >
            Invite Member
          </Button>
        </div>
      </div>

      {/* TAB 1: ACTIVE MEMBERS */}
      {activeTab === 'members' && (
        <Card padding="20px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ maxWidth: '300px', flex: 1 }}>
              <Input
                placeholder="Filter members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-divider)', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' }}>
                <th style={{ padding: '10px 14px' }}>User</th>
                <th style={{ padding: '10px 14px', width: '160px' }}>Role</th>
                <th style={{ padding: '10px 14px', width: '140px' }}>Joined Date</th>
                <th style={{ padding: '10px 14px', width: '100px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => (
                <tr key={m.membershipId || m.user.id} style={{ borderBottom: '1px solid var(--border-divider)' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <img
                        src={m.user.avatar}
                        alt={m.user.name}
                        style={{ width: '34px', height: '34px', borderRadius: '999px', objectFit: 'cover' }}
                      />
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{m.user.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {m.role === 'owner' ? (
                      <Badge variant="teal" size="sm">Owner</Badge>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) => updateMemberRole(m.user.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: 'var(--surface-recessed)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="guest">Guest</option>
                      </select>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', color: 'var(--text-muted)' }}>
                    {m.joinedAt || '2026-08-01'}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                    {m.role !== 'owner' && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove ${m.user.name} from workspace?`)) {
                            removeMember(m.user.id);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-danger)',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px'
                        }}
                        title="Remove member"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* TAB 2: PENDING INVITATIONS */}
      {activeTab === 'invites' && (
        <Card padding="20px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700' }}>Pending Invitations</h3>
            <Button variant="primary" size="sm" icon={<UserPlus size={14} />} onClick={() => setIsInviteModalOpen(true)}>
              Invite New User
            </Button>
          </div>

          {pendingInvites.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              No pending invitations. All invited members have joined.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-divider)', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' }}>
                  <th style={{ padding: '10px 14px' }}>Recipient Email</th>
                  <th style={{ padding: '10px 14px' }}>Assigned Role</th>
                  <th style={{ padding: '10px 14px' }}>Invited Date</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-divider)' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      {inv.email}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge variant={getRoleBadgeVariant(inv.role)} size="xs">
                        {inv.role}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-muted)' }}>
                      {inv.invitedAt}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => revokeInvite(inv.id)}
                        style={{ color: 'var(--color-danger)' }}
                      >
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* TAB 3: TEAMS & GROUPS */}
      {activeTab === 'teams' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700' }}>Workspace Teams</h3>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsTeamModalOpen(true)}>
              Create Team
            </Button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {teams.map((tm) => (
              <Card key={tm.id} padding="20px">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div
                    style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--surface-recessed)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-teal)'
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                      {tm.icon || 'group'}
                    </span>
                  </div>
                  <Badge variant="teal" size="xs">{tm.membersCount || 1} Members</Badge>
                </div>
                <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>{tm.name}</h4>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  Workspace team group for notifications and project assignment
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* CREATE TEAM MODAL */}
      <Modal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        title="Create Team Group"
        subtitle="Group collaborators to assign tasks and manage module visibility"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsTeamModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateTeamSubmit}>
              Create Team
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateTeamSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Team Name *"
            placeholder="e.g., Backend Squad, QA & Testing"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            autoFocus
            required
          />

          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Team Icon
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {teamIcons.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => setTeamIcon(ic)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: teamIcon === ic ? 'rgba(35, 190, 179, 0.18)' : 'var(--surface-recessed)',
                    border: teamIcon === ic ? '2px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                    color: teamIcon === ic ? 'var(--accent-teal)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                    {ic}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};
